import type {
  CourseCashReceipt,
  CourseCashTransaction,
} from 'tio-common/types/course-cash-transaction';
import type TasAssistanceModel from 'tio-data/models/tas-assistance';
import type ApprovableModel from 'tio-data/models/tas-approvable';

/**
 * Transaction Adapter
 *
 * Transforms backend TasAssistance models into the UI's CourseCashTransaction shape.
 * This adapter provides a single point to map from TasAssistance records with
 * CAPTURED state to the common transaction interface.
 */

/**
 * Map TasAssistance records (Course Cash with CAPTURED state) to UI transaction shape.
 *
 * @param assistances - Array of TasAssistance models from the application
 * @param approvablesByAssistance - Map of assistance ID → approvable models,
 *   queried directly from the /tas-approvables endpoint. When provided, takes
 *   precedence over the sideloaded relationship data on each assistance.
 * @returns Array of CourseCashTransaction for UI consumption
 */
export function mapAssistancesToTransactions(
  assistances: TasAssistanceModel[],
  approvablesByAssistance?: Map<string, ApprovableModel[]>
): CourseCashTransaction[] {
  return assistances
    .filter((a): a is TasAssistanceModel & { id: string } =>
      Boolean(a.isCourseCash && a.isCaptured && a.id)
    )
    .map((assistance) => {
      // Prefer endpoint-queried approvables; fall back to sideloaded relationship data
      const approvables =
        approvablesByAssistance?.get(assistance.id) ?? assistance.courseCashApprovables ?? [];
      let receipts: CourseCashReceipt[];

      if (approvables.length > 0) {
        receipts = mapReceiptsFromApprovables(assistance, approvables);
      } else {
        // Legacy fallback for pre-migration data
        console.warn(
          `[CourseCash Fallback] Assistance ${assistance.id} has no TasApprovable records. ` +
            `Using legacy attachment-based logic. This assistance needs migration.`
        );
        receipts = mapReceiptsFromAttachments(assistance);
      }

      const receiptStatus = determineReceiptStatus(receipts, approvables);
      const capturedDate = assistance.capturedDate;
      const dueDate = assistance.receiptDueDate;

      return {
        id: assistance.id,
        transactionDate: capturedDate?.toISOString().split('T')[0] ?? '',
        merchantName: extractMerchantName(assistance),
        amount: Math.abs(assistance.amount ?? 0),
        grossAmount: assistance.grossAmount != null ? Math.abs(assistance.grossAmount) : undefined,
        receiptStatus,
        receiptDueDate: dueDate?.toISOString().split('T')[0],
        receipts,
        description: extractDescription(assistance),
        category: 'Education',
        assistanceId: assistance.id,
        lineItems: [],
      };
    });
}

/**
 * Determine receipt status from approvable state (primary) or receipt presence (fallback).
 *
 * Priority: approvable state > receipt count > 'needed'
 */
export function determineReceiptStatus(
  receipts: CourseCashReceipt[],
  approvables: ApprovableModel[]
): CourseCashTransaction['receiptStatus'] {
  if (receipts.length === 0 && approvables.length === 0) {
    return 'needed';
  }

  // When approvables exist, derive status from their state
  const courseCashApprovables = approvables.filter((a) => a.isCourseCashUsageReview);

  if (courseCashApprovables.length === 0) {
    // Receipts exist but approvable hasn't been created yet (Sidekiq job lag)
    return receipts.length > 0 ? 'uploaded' : 'needed';
  }

  // If ANY approvable is approved/affirmed, the transaction is verified
  if (courseCashApprovables.some((a) => a.isApproved)) return 'verified';

  // If ANY is in PREPARING state (changes needed), allow re-upload
  if (courseCashApprovables.some((a) => a.isPreparing)) return 'changes_needed';

  // If all are abandoned, the user removed previous receipts — still needs a new one
  const allAbandoned = courseCashApprovables.every((a) => a.isAbandoned);
  if (allAbandoned) return 'needed';

  // If a non-abandoned, non-rejected approvable exists (e.g. new upload after rejection),
  // the transaction is in review — don't let an old rejected approvable override it.
  const hasActivePending = courseCashApprovables.some(
    (a) => !a.isAbandoned && !a.isRejected && !a.isApproved
  );
  if (hasActivePending) return 'uploaded';

  // Only show rejected when ALL non-abandoned approvables are rejected (no new upload yet)
  if (courseCashApprovables.some((a) => a.isRejected)) return 'rejected';

  // Default: in review (PENDING)
  return 'uploaded';
}

/**
 * Map approvable records to CourseCashReceipt shape.
 * Each approvable links to a TasAsset (the receipt file) via its polymorphic `approvable` relationship.
 *
 * NOTE: We use belongsTo('approvable').id() + assistance.tasAssets lookup instead of
 * traversing approvable.approvable directly, because the backend returns the relationship
 * linkage with type "digital-assets" (base STI type) while the included record serializes
 * as "tas-assets" (subtype). This type mismatch prevents Ember Data from resolving the
 * sync polymorphic relationship.
 *
 * @param assistance - The parent assistance, used to look up loaded TasAsset records
 * @param approvables - Approvable models for this assistance, from endpoint or relationship
 */
export function mapReceiptsFromApprovables(
  assistance: TasAssistanceModel,
  approvables: ApprovableModel[]
): CourseCashReceipt[] {
  return approvables
    .filter((approvable) => !approvable.isAbandoned)
    .map((approvable) => {
      // Get the TasAsset ID from the raw relationship data without traversing the
      // polymorphic relationship (which fails due to digital-assets vs tas-assets STI mismatch)
      // @ts-expect-error - belongsTo() typed as 'never' due to polymorphic empty-string type declaration
      const assetId = approvable.belongsTo('approvable').id() as string | null;
      const tasAsset = assistance.tasAssets?.find((a) => a.id === assetId);
      const attachments = tasAsset?.attachments ?? [];
      const attachment = attachments[0];

      return {
        id: approvable.id ?? `approvable-${Date.now()}`,
        attachmentId: attachment?.id ?? undefined,
        assetId: assetId ?? undefined,
        approvableId: approvable.id ?? undefined,
        approvableState: approvable.state,
        filename: attachment?.filename ?? attachment?.name ?? 'Receipt',
        contentType: attachment?.contentType,
        uploadedAt: approvable.createdAt ?? new Date().toISOString(),
        receiptUrl: attachment?.signedUrl ?? '',
        status: mapApprovableStateToReceiptStatus(approvable.state),
      };
    });
}

/**
 * Map an approvable AASM state to the CourseCashReceipt status.
 */
export function mapApprovableStateToReceiptStatus(state: string): CourseCashReceipt['status'] {
  switch (state) {
    case 'ApprovableState.APPROVED':
      return 'verified';
    case 'ApprovableState.REJECTED':
      return 'rejected';
    case 'ApprovableState.PREPARING':
      return 'changes_needed';
    // AFFIRMED/NOT_AFFIRMED are transitory states that auto-cascade;
    // they should never appear at rest, but default handles them.
    default:
      return 'pending_verification';
  }
}

/**
 * Legacy: Map receipts from CARD_USAGE_PROOF attachments.
 * Used as fallback for pre-migration assistances without TasApprovable records.
 */
export function mapReceiptsFromAttachments(assistance: TasAssistanceModel): CourseCashReceipt[] {
  const attachments = assistance.attachments ?? [];
  const cardUsageProofAttachments = attachments.filter((attachment) => {
    const isCardUsageProof = attachment.displayClassification === 'CARD_USAGE_PROOF';
    const isDeleted = Boolean(attachment.attachmentDeletedAt);
    return isCardUsageProof && !isDeleted;
  });

  const receipts: CourseCashReceipt[] = [];

  for (const attachment of cardUsageProofAttachments) {
    if (!attachment.id) {
      console.warn('Skipping receipt attachment without id', { attachment });
      continue;
    }

    const filename = attachment.filename || attachment.name || 'Receipt';
    const uploadedAt = attachment.createdAt || new Date().toISOString();

    receipts.push({
      id: attachment.id,
      attachmentId: attachment.id,
      assetId: attachment.digitalAsset?.id ?? undefined,
      filename,
      contentType: attachment.contentType,
      uploadedAt,
      receiptUrl: attachment.signedUrl || attachment.digitalAsset?.signedUrl || '',
      status: 'pending_verification',
    });
  }

  return receipts;
}

/**
 * Extract merchant name from the program instance's SCHOOL_INSTITUTION_NAME field.
 * Falls back to 'Merchant' if the field is not set.
 */
export function extractMerchantName(assistance: TasAssistanceModel): string {
  const institutionName = assistance.tasApplication?.tasProgramInstance?.institutionName;
  if (institutionName) return institutionName;

  return 'Merchant';
}

/**
 * Extract transaction description
 */
export function extractDescription(assistance: TasAssistanceModel): string | undefined {
  return assistance.fields?.description as string | undefined;
}

/**
 * Transforms PcsAuthorization model to CourseCashTransaction
 *
 * @deprecated Use mapAssistancesToTransactions instead - this is a placeholder
 * @param authorization - The PcsAuthorization model from Ember Data
 * @returns CourseCashTransaction - Transformed transaction data
 */
export function fromPcsAuthorization(authorization: unknown): CourseCashTransaction {
  // TODO: Replace with actual model mapping when PcsAuthorization is available
  // This is a placeholder structure to show expected transformation
  const auth = authorization as Record<string, unknown>;
  const receiptUrl = auth.receiptUrl as string | undefined;
  const receipts: CourseCashReceipt[] = receiptUrl
    ? [
        {
          id: `legacy-${String(auth.id ?? '')}`,
          attachmentId: undefined,
          assetId: undefined,
          filename: 'Receipt',
          uploadedAt: new Date().toISOString(),
          receiptUrl,
          status: 'pending_verification',
        },
      ]
    : [];

  return {
    id: (auth.id as string) || '',
    transactionDate: (auth.createdAt as string) || new Date().toISOString(),
    merchantName: (auth.merchantName as string) || 'Merchant',
    amount: parseFloat((auth.amount as string) || '0'),
    receiptStatus: mapReceiptStatus(auth.receiptStatus as string | undefined),
    receiptDueDate: auth.receiptDueDate as string | undefined,
    receipts,
    description: auth.description as string | undefined,
    category: auth.category as string | undefined,
    cardId: auth.cardId as string | undefined,
    lineItems: [],
  };
}

/**
 * Maps backend receipt status to UI receipt status
 */
function mapReceiptStatus(
  backendStatus: string | undefined
): CourseCashTransaction['receiptStatus'] {
  switch (backendStatus?.toLowerCase()) {
    case 'needed':
    case 'pending':
      return 'needed';
    case 'uploaded':
    case 'submitted':
      return 'uploaded';
    case 'verified':
    case 'approved':
      return 'verified';
    case 'not_required':
    case 'not-required':
    case 'na':
      return 'not_required';
    default:
      return 'needed'; // Default to needed if status is unclear
  }
}

/**
 * Filters transactions that need receipt upload
 */
export function getUnverifiedTransactions(
  transactions: CourseCashTransaction[]
): CourseCashTransaction[] {
  return transactions.filter((t) => t.receiptStatus === 'needed');
}

/**
 * Groups transactions by receipt due date
 */
export function groupByDueDate(
  transactions: CourseCashTransaction[]
): Map<string, CourseCashTransaction[]> {
  const grouped = new Map<string, CourseCashTransaction[]>();

  for (const transaction of transactions) {
    const dueDate = transaction.receiptDueDate || 'No due date';
    const existing = grouped.get(dueDate) || [];
    existing.push(transaction);
    grouped.set(dueDate, existing);
  }

  return grouped;
}

/**
 * Sorts transactions by date (newest first)
 */
export function sortByDate(transactions: CourseCashTransaction[]): CourseCashTransaction[] {
  return [...transactions].sort((a, b) => {
    const dateA = new Date(a.transactionDate).getTime();
    const dateB = new Date(b.transactionDate).getTime();
    return dateB - dateA; // Newest first
  });
}
